import { useTranslation } from 'react-i18next'
import { RepeatIcon } from '@/ui/icons/repeat'
import { useAppSelector } from '@/redux/hooks'
import { useCart } from '@/hooks/use-cart'
import EmptyCart from '@/app/full-catalog/empty-cart'
import { NewItem } from '@/types/new-item'
import { CartItem } from './cart-item'
import { LongArrowRightDown } from '@/ui/icons/long-arrow-right-down'

interface CartProps {
  currentReturnedItemId: string
  onClose: () => void
}

export const Cart = ({ currentReturnedItemId, onClose }: CartProps) => {
  const { t } = useTranslation()

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const orderItems = useAppSelector((store) => store.order.orderProducts)

  const { cartItems, getCartTotal } = useCart()

  const cartTotalQuantity = getCartTotal(cartItems)

  const groupedCartItems = cartItems.reduce<Record<string, NewItem[]>>(
    (acc, item) => {
      if (!acc[item.returnedItemId]) {
        acc[item.returnedItemId] = []
      }
      acc[item.returnedItemId].push(item)
      return acc
    },
    {}
  )

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (isHoff) {
    return (
      <div className="flex-1 pb-6 pt-4">
        {cartTotalQuantity > 0 ? (
          <ul className="space-y-8">
            {Object.entries(groupedCartItems).map(
              ([returnedItemId, newItems]) => {
                const returnedItemName =
                  orderItems.find(
                    (orderItem) => orderItem.lineItemId === returnedItemId
                  )?.product.name || 'Unknown Item'

                return (
                  <li key={returnedItemId}>
                    <p className="flex items-center gap-1 pb-3 font-normal text-neutral-700">
                      {returnedItemName}
                      <LongArrowRightDown width={20} height={20} />
                    </p>
                    <ul className="divide-y-1 divide-neutral-900 border-y border-solid border-neutral-900">
                      {newItems.map((newItem) => {
                        return (
                          <li
                            key={newItem.productVariant.variantId}
                            className="group"
                          >
                            <CartItem
                              item={newItem}
                              currentReturnedItemId={currentReturnedItemId}
                            />
                          </li>
                        )
                      })}
                    </ul>
                  </li>
                )
              }
            )}
          </ul>
        ) : (
          <div className="size-full pt-10">
            <h3 className="text-center text-xl font-medium">
              {t('full_catalog.no_items_added')}
            </h3>
          </div>
        )}
      </div>
    )
  }

  if (isMisako) {
    return (
      <div className="flex-1 pb-6 pt-4">
        {cartTotalQuantity > 0 ? (
          <div className="space-y-4">
            <div className="flex justify-between gap-4">
              <h2 className="text-[1.75rem] font-medium leading-normal">
                {t('full_catalog.your_selected_items')}
              </h2>
            </div>
            <ul className="space-y-8">
              {Object.entries(groupedCartItems).map(
                ([returnedItemId, newItems]) => {
                  const returnedItemName =
                    orderItems.find(
                      (orderItem) => orderItem.lineItemId === returnedItemId
                    )?.product.name || 'Unknown Item'

                  return (
                    <li key={returnedItemId}>
                      <p className="flex items-center gap-1 pb-3 font-normal text-neutral-700">
                        {returnedItemName}
                        <LongArrowRightDown width={20} height={20} />
                      </p>
                      <ul className="divide-y-1 divide-neutral-900 border-y border-solid border-neutral-900">
                        {newItems.map((newItem) => {
                          return (
                            <li
                              key={newItem.productVariant.variantId}
                              className="group"
                            >
                              <CartItem
                                item={newItem}
                                currentReturnedItemId={currentReturnedItemId}
                              />
                            </li>
                          )
                        })}
                      </ul>
                    </li>
                  )
                }
              )}
            </ul>
          </div>
        ) : (
          <div className="size-full pt-10">
            <h3 className="text-center text-base font-medium">
              {t('full_catalog.no_items_added')}
            </h3>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col pb-6 md:px-5">
      {cartTotalQuantity > 0 ? (
        <ul className="space-y-7">
          {Object.entries(groupedCartItems).map(
            ([returnedItemId, newItems]) => {
              const returnedItemName =
                orderItems.find(
                  (orderItem) => orderItem.lineItemId === returnedItemId
                )?.product.name || 'Unknown Item'

              return (
                <li key={returnedItemId}>
                  <p className="flex items-center gap-2 text-base text-neutral-600">
                    <RepeatIcon />
                    {returnedItemName}
                  </p>
                  <ul className="space-y-3.5 pt-3">
                    {newItems.map((newItem) => {
                      return (
                        <li
                          key={newItem.productVariant.variantId}
                          className="group"
                        >
                          <CartItem
                            item={newItem}
                            currentReturnedItemId={currentReturnedItemId}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            }
          )}
        </ul>
      ) : (
        <div className="flex size-full flex-1 flex-col items-center justify-center">
          <div className="relative flex h-[190px] w-[400px] items-center justify-center gap-1 overflow-hidden">
            <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-r from-transparent to-white md:to-[#F2F2F2]" />
            <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-l from-transparent to-white md:to-[#F2F2F2]" />
            {[...Array(3)].map((_, index) => (
              <div key={index}>
                <EmptyCart
                  lightColor="var(--brand-600)"
                  darkColor="var(--brand-800)"
                />
              </div>
            ))}
          </div>
          <div className="space-y-1 pt-4">
            <h3 className="text-center text-xl font-semibold">
              {t('full_catalog.no_items_added')}
            </h3>
            <p className="mx-auto max-w-80 text-center text-sm font-light text-slate-500">
              {t('full_catalog.empty_cart_description')}
            </p>
          </div>
          <div className="pt-6">
            <button
              className="flex items-center justify-center rounded-full bg-[radial-gradient(160.21%_97.87%_at_50%_100%,var(--brand-600)_0%,var(--brand-800)_100%)] px-6 py-2.5 font-semibold text-white shadow-sm ring-1 ring-brand-700 transition-all hover:shadow-md"
              onClick={onClose}
            >
              {t('full_catalog.shop_now')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
